/**
 * DynamicConfigSelector Component
 * Model and configuration selection with dynamic options from API
 */

import { Select } from '@/components/ui/Select';
import { ModelSelector } from './ModelSelector';
import type { ImageModelInfo, ImageGenerationConfig } from '@/types/playground';

interface DynamicConfigSelectorProps {
  models: ImageModelInfo[];
  config: ImageGenerationConfig;
  currentModel: ImageModelInfo | undefined;
  operationMode: 'generate' | 'edit' | 'fuse';
  onModelChange: (modelId: string) => void;
  onSizeChange: (size: string) => void;
  onAspectRatioChange: (ratio: string) => void;
  onQualityChange: (quality: string | null) => void;
  onWebSearchChange: (enabled: boolean) => void;
  onBackgroundChange?: (background: string | null) => void;
  compact?: boolean;
  isLoading?: boolean;
}

/**
 * Get display label for aspect ratio
 */
function getAspectRatioLabel(ratio: string): string {
  const labels: Record<string, string> = {
    '1:1': '1:1 (正方形)',
    '3:4': '3:4 (肖像)',
    '4:3': '4:3 (风景)',
    '9:16': '9:16 (竖屏)',
    '16:9': '16:9 (宽屏)',
    '2:3': '2:3 (长肖像)',
    '3:2': '3:2 (长风景)',
    '4:5': '4:5 (社交)',
    '5:4': '5:4',
    '21:9': '21:9 (超宽)',
    '1:2': '1:2 (长图)',
    '2:1': '2:1 (横幅)',
  };
  return labels[ratio] || ratio;
}

/**
 * Get display label for size
 */
function getSizeLabel(size: string): string {
  const labels: Record<string, string> = {
    '1K': '1K (1024px)',
    '2K': '2K (2048px)',
    '4K': '4K (4096px)',
    '1024x1024': '1024×1024',
    '1024x1792': '1024×1792',
    '1792x1024': '1792×1024',
    '512x512': '512×512',
    '256x256': '256×256',
    'auto': '自动',
  };
  return labels[size] || size;
}

/**
 * Get display label for quality
 */
function getQualityLabel(quality: string): string {
  const labels: Record<string, string> = {
    'standard': '标准',
    'hd': '高清',
    'low': '低',
    'medium': '中',
    'high': '高',
    'auto': '自动',
  };
  return labels[quality] || quality;
}

export function DynamicConfigSelector({
  models,
  config,
  currentModel,
  operationMode,
  onModelChange,
  onSizeChange,
  onAspectRatioChange,
  onQualityChange,
  onWebSearchChange,
  onBackgroundChange,
  compact = false,
  isLoading = false,
}: DynamicConfigSelectorProps) {
  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-10 bg-gray-200 rounded-lg"></div>
        <div className="grid grid-cols-2 gap-4">
          <div className="h-10 bg-gray-200 rounded-lg"></div>
          <div className="h-10 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex flex-wrap gap-3 items-center">
        {/* Model selector (compact) */}
        <div className="min-w-[160px]">
          <ModelSelector
            models={models}
            selectedModel={config.model}
            onModelChange={onModelChange}
            operationMode={operationMode}
            compact
          />
        </div>

        {/* Size selector */}
        {currentModel && currentModel.sizes.length > 0 && (
          <div className="flex rounded-lg overflow-hidden border border-gray-200">
            {currentModel.sizes.map((size) => (
              <button
                key={size}
                type="button"
                onClick={() => onSizeChange(size)}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  config.size === size
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {size}
              </button>
            ))}
          </div>
        )}

        {/* Aspect ratio selector (compact dropdown) */}
        {currentModel && currentModel.aspect_ratios.length > 0 && (
          <select
            value={config.aspect_ratio}
            onChange={(e) => onAspectRatioChange(e.target.value)}
            className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-primary-500"
          >
            {currentModel.aspect_ratios.map((ratio) => (
              <option key={ratio} value={ratio}>
                {ratio}
              </option>
            ))}
          </select>
        )}

        {/* Web search toggle */}
        {currentModel?.supports_web_search && (
          <label className="flex items-center gap-1.5 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={config.web_search}
              onChange={(e) => onWebSearchChange(e.target.checked)}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="text-gray-700">网络搜索</span>
          </label>
        )}
      </div>
    );
  }

  // Full config panel
  return (
    <div className="space-y-5">
      {/* Model Selection */}
      <ModelSelector
        models={models}
        selectedModel={config.model}
        onModelChange={onModelChange}
        operationMode={operationMode}
      />

      {/* Size and Aspect Ratio */}
      {currentModel && (
        <div className="grid grid-cols-2 gap-4">
          {/* Size Selection */}
          {currentModel.sizes.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                分辨率
              </label>
              <div className="flex flex-wrap rounded-lg overflow-hidden border border-gray-200">
                {currentModel.sizes.map((size) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => onSizeChange(size)}
                    className={`flex-1 min-w-[60px] px-3 py-2 text-sm font-medium transition-colors ${
                      config.size === size
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {getSizeLabel(size)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Aspect Ratio Selection */}
          {currentModel.aspect_ratios.length > 0 && (
            <Select
              label="宽高比"
              value={config.aspect_ratio}
              onChange={(e) => onAspectRatioChange(e.target.value)}
              options={currentModel.aspect_ratios.map((ratio) => ({
                value: ratio,
                label: getAspectRatioLabel(ratio),
              }))}
            />
          )}
        </div>
      )}

      {/* Quality and Background (if supported) */}
      {currentModel && (currentModel.quality_options.length > 0 || currentModel.supports_transparency) && (
        <div className="grid grid-cols-2 gap-4">
          {/* Quality Selection */}
          {currentModel.quality_options.length > 0 && (
            <Select
              label="质量"
              value={config.quality || ''}
              onChange={(e) => onQualityChange(e.target.value || null)}
              options={[
                { value: '', label: '默认' },
                ...currentModel.quality_options.map((q) => ({
                  value: q,
                  label: getQualityLabel(q),
                })),
              ]}
            />
          )}

          {/* Background (for transparency support) */}
          {currentModel.supports_transparency && onBackgroundChange && (
            <Select
              label="背景"
              value={config.background || ''}
              onChange={(e) => onBackgroundChange(e.target.value || null)}
              options={[
                { value: '', label: '默认' },
                { value: 'transparent', label: '透明' },
                { value: 'white', label: '白色' },
                { value: 'auto', label: '自动' },
              ]}
            />
          )}
        </div>
      )}

      {/* Web Search Toggle */}
      {currentModel?.supports_web_search && (
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
          <input
            type="checkbox"
            id="web_search"
            checked={config.web_search}
            onChange={(e) => onWebSearchChange(e.target.checked)}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <div>
            <label htmlFor="web_search" className="text-sm font-medium text-gray-700 cursor-pointer">
              启用网络搜索
            </label>
            <p className="text-xs text-gray-500">
              允许模型搜索网络获取参考素材
            </p>
          </div>
        </div>
      )}

      {/* Model capabilities summary */}
      {currentModel && (
        <div className="text-xs text-gray-500 flex flex-wrap gap-2">
          {currentModel.max_n > 1 && (
            <span className="px-2 py-1 bg-gray-100 rounded">
              最多生成 {currentModel.max_n} 张
            </span>
          )}
          {operationMode === 'fuse' && currentModel.max_images_fuse > 0 && (
            <span className="px-2 py-1 bg-gray-100 rounded">
              最多融合 {currentModel.max_images_fuse} 张
            </span>
          )}
          {currentModel.output_formats.length > 1 && (
            <span className="px-2 py-1 bg-gray-100 rounded">
              输出: {currentModel.output_formats.join(', ')}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
